import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Drawer from '../../components/Drawer';
import Spinner from '../../components/Spinner';
import {
  fosApi, fosError, formatDate, isVersionConflict, newRequestId, ROLE_HINT, ROLE_LABEL, scopeFromKey,
  type AssignmentRole, type AssignmentView, type DonorDetail, type RelationshipView,
} from '../../lib/fosApi';
import AssignDrawer, { type AssignMode } from './AssignDrawer';
import DonorFormDrawer from './DonorFormDrawer';
import { useFos } from './FosShell';
import Interactions from './Interactions';
import LedgerPanel from './LedgerPanel';
import PersonLinkPanel from './PersonLinkPanel';
import { Avatar, EmptyState, ErrorNote, HealthChips, Icon, KindChip, Section, StatusChip } from './ui';

const ROLES: AssignmentRole[] = ['primary_coordinator', 'manager'];

function AssignmentRow({ a, rel, onMode }: { a: AssignmentView; rel: RelationshipView; onMode: (m: AssignMode) => void }) {
  const manage = rel.permissions.manageAssignments && rel.status === 'active';
  return (
    <div className={`fos-assignee${a.inEffect ? '' : ' not-in-effect'}`}>
      <Avatar name={a.assignee.name} size={38} />
      <div className="fos-assignee-main">
        <div className="fos-assignee-name"><strong>{a.assignee.name}</strong>{a.assignee.ref && <span className="fos-ref">{a.assignee.ref}</span>}</div>
        <div className="subtitle">{a.assignee.positions.length ? a.assignee.positions.join(' · ') : 'No current Position'}</div>
        <div className="subtitle">Since {formatDate(a.startedAt)}{a.endsOn ? ` · planned end ${formatDate(a.endsOn)}` : ''}{a.basisPositionName ? ` · as ${a.basisPositionName}` : ''}{!a.inEffect && !a.endedAt ? ' · not in effect' : ''}</div>
        <div className="fos-chips"><HealthChips flags={a.health} /></div>
      </div>
      {manage && (
        <div className="fos-assignee-actions">
          <button className="btn small" onClick={() => onMode({ kind: 'reassign', assignment: a })}><Icon name="swap" size={13} /> Reassign</button>
          <button className="linkbtn" onClick={() => onMode({ kind: 'dates', assignment: a })}>Dates</button>
          <button className="linkbtn danger" onClick={() => onMode({ kind: 'end', assignment: a })}>End</button>
        </div>
      )}
    </div>
  );
}

function Assignments({ rel, onMode }: { rel: RelationshipView; onMode: (m: AssignMode) => void }) {
  const [showHistory, setShowHistory] = useState(false);
  const canManage = rel.permissions.manageAssignments && rel.status === 'active';
  return (
    <>
      <div className="fos-roles">
        {ROLES.map((role) => {
          const held = rel.activeAssignments.filter((a) => a.role === role);
          return (
            <div key={role} className="fos-role">
              <div className="fos-role-head"><div><strong>{ROLE_LABEL[role]}</strong><small>{ROLE_HINT[role]}</small></div>
                {canManage && (role === 'manager' || held.length === 0) && <button className="btn small" onClick={() => onMode({ kind: 'assign', role })}><Icon name="plus" size={13} /> Assign</button>}</div>
              {held.length === 0 ? <div className="fos-unassigned">Unassigned</div> : held.map((a) => <AssignmentRow key={a.id} a={a} rel={rel} onMode={onMode} />)}
            </div>
          );
        })}
      </div>
      {rel.assignmentHistory.length > 0 && (
        <div className="fos-history">
          <button className="linkbtn" onClick={() => setShowHistory((s) => !s)}>{showHistory ? 'Hide' : 'Show'} assignment history ({rel.assignmentHistory.length})</button>
          {showHistory && (
            <ul>
              {rel.assignmentHistory.map((a) => (
                <li key={a.id}><strong>{a.assignee.name}</strong> <span className="subtitle">· {ROLE_LABEL[a.role]} · {formatDate(a.startedAt)} → {a.endedAt ? formatDate(a.endedAt) : a.endsOn ? formatDate(a.endsOn) : '—'}{a.endReason ? ` · ${a.endReason}` : ''}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

function RelationshipSummary({ donorId, rel, onChanged }: { donorId: string; rel: RelationshipView; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rel.summary ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setText(rel.summary ?? ''); setEditing(false); }, [rel.id, rel.summary]);
  if (rel.summary === null) return null;
  const save = async () => {
    setBusy(true); setError('');
    try { await fosApi.saveRelationship({ requestId: newRequestId(), operation: 'update', donorId, scope: scopeFromKey(rel.scopeKey), expectedVersion: rel.version, summary: text }); setEditing(false); onChanged(); }
    catch (cause) { const c = fosError(cause); setError(isVersionConflict(c) ? 'This relationship changed. Reload the page.' : c.message); } finally { setBusy(false); }
  };
  return (
    <Section title="About this relationship" icon="note" action={rel.permissions.manageInteractions && !editing ? <button className="linkbtn" onClick={() => setEditing(true)}>Edit</button> : null}>
      {editing ? (
        <>
          <textarea className="fos-summary-edit" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} />
          <ErrorNote message={error} />
          <div className="fos-composer-actions"><button className="btn small" onClick={() => setEditing(false)}>Cancel</button><button className="btn primary small" onClick={save} disabled={busy}>{busy ? <Spinner /> : 'Save'}</button></div>
        </>
      ) : rel.summary ? <p className="fos-lede">{rel.summary}</p> : <p className="fos-hint">No summary yet. Private to the {rel.scopeLabel} relationship.</p>}
      <p className="fos-privacy"><Icon name="lock" size={12} /> Private to the {rel.scopeLabel} relationship — other districts cannot see this.</p>
    </Section>
  );
}

export default function FosDonorDetail() {
  const { donorId = '' } = useParams();
  const { context } = useFos();
  const [detail, setDetail] = useState<DonorDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('');
  const [mode, setMode] = useState<AssignMode | null>(null);
  const [editing, setEditing] = useState(false);
  const [addingRel, setAddingRel] = useState(false);
  const [relBusy, setRelBusy] = useState('');
  const [relError, setRelError] = useState('');

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError('');
    try { setDetail(await fosApi.get({ donorId })); } catch (cause) { setDetail(null); setError(fosError(cause).message); } finally { setLoading(false); }
  }, [donorId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (detail && !detail.relationships.some((r) => r.scopeKey === selected)) setSelected(detail.relationships[0]?.scopeKey ?? ''); }, [detail, selected]);

  if (loading && !detail) return <div className="fos-loading"><Spinner label="Loading donor" /></div>;
  if (error || !detail) {
    return (
      <div className="fos-card">
        <EmptyState icon="lock" title="This donor is not available">You may not have access to it, or it may not exist. <Link to="/fos/donors">Back to the directory</Link></EmptyState>
      </div>
    );
  }
  const { donor, permissions } = detail;
  const rel = detail.relationships.find((r) => r.scopeKey === selected) ?? detail.relationships[0];
  const refresh = () => void load(true);
  const setRelStatus = async (r: RelationshipView, status: 'active' | 'inactive') => {
    setRelBusy(r.id); setRelError('');
    try { await fosApi.saveRelationship({ requestId: newRequestId(), operation: 'update', donorId: donor.id, scope: scopeFromKey(r.scopeKey), expectedVersion: r.version, status }); refresh(); }
    catch (cause) { setRelError(fosError(cause).message); } finally { setRelBusy(''); }
  };
  const addr = donor.contact?.address;

  return (
    <div className="fos-detail">
      <Link to="/fos/donors" className="fos-back">← All donors</Link>
      <header className="fos-donor-head">
        <Avatar name={donor.displayName} size={64} />
        <div className="fos-donor-title">
          <h2>{donor.displayName}</h2>
          <div className="fos-chips"><span className="fos-ref">{donor.ref}</span><KindChip kind={donor.kind} /><StatusChip status={donor.status} />
            {donor.personLink && <span className="fos-chip link"><Icon name="link" size={12} />Compass person</span>}
            {donor.possibleDuplicate && <span className="fos-chip attention"><Icon name="alert" size={12} />Possible duplicate — review</span>}
            {donor.primaryDistrict && <span className="fos-chip neutral">Main district: {donor.primaryDistrict.name}</span>}</div>
        </div>
        {permissions.editIdentity && <button className="btn" onClick={() => setEditing(true)}><Icon name="edit" size={14} /> Edit donor</button>}
      </header>
      {donor.preferences?.doNotContact && <div className="fos-banner"><Icon name="alert" size={16} /> This donor has asked not to be contacted. Do not send them Communications or reach out to them.</div>}
      {donor.preferences?.doNotSolicit && !donor.preferences.doNotContact && <div className="fos-banner"><Icon name="alert" size={16} /> This donor has asked not to be solicited. Do not ask them for a contribution.</div>}

      <div className="fos-grid">
        <div className="fos-main">
          <div className="fos-rel-tabs" role="tablist" aria-label="Relationships">
            {detail.relationships.map((r) => (
              <button key={r.id} role="tab" aria-selected={r.scopeKey === rel.scopeKey} className={`fos-rel-tab${r.scopeKey === rel.scopeKey ? ' active' : ''}${r.status === 'inactive' ? ' off' : ''}`} onClick={() => setSelected(r.scopeKey)}>
                {r.scopeLabel}{r.status === 'inactive' ? ' (inactive)' : ''}
              </button>
            ))}
            {permissions.addRelationshipScopes.length > 0 && <button className="fos-rel-tab add" onClick={() => setAddingRel(true)}><Icon name="plus" size={13} /> Add relationship</button>}
          </div>

          {rel && (
            <>
              {rel.status === 'inactive' && (
                <div className="fos-banner muted">This relationship is inactive.
                  {rel.permissions.manageDonors && <button className="linkbtn" disabled={relBusy === rel.id} onClick={() => setRelStatus(rel, 'active')}>Reactivate</button>}</div>
              )}
              <Section title="People responsible" icon="users"><Assignments rel={rel} onMode={setMode} /></Section>
              <RelationshipSummary donorId={donor.id} rel={rel} onChanged={refresh} />
              {rel.interactions !== null ? (
                <Section title="Activity" icon="clock"><Interactions donorId={donor.id} relationship={rel} onChanged={refresh} /></Section>
              ) : (
                <Section title="Activity" icon="clock"><p className="fos-hint"><Icon name="lock" size={13} /> You can see this donor, but not the notes and activity in this relationship.</p></Section>
              )}
              {rel.permissions.manageDonors && rel.status === 'active' && (
                <p className="fos-fine"><button className="btn small quiet" disabled={relBusy === rel.id} onClick={() => setRelStatus(rel, 'inactive')}>Mark this relationship inactive</button></p>
              )}
              <ErrorNote message={relError} />
            </>
          )}
          {detail.relationships.some((r) => r.permissions.viewHistory) && <LedgerPanel donorId={donor.id} donorName={donor.displayName} />}
        </div>

        <aside className="fos-aside">
          <Section title="Contact" icon="mail">
            {donor.contact ? (
              <ul className="fos-contact">
                {donor.contact.emails.map((e) => <li key={e.normalized}><Icon name="mail" size={14} /><a href={`mailto:${e.value}`}>{e.value}</a>{e.primary && donor.contact!.emails.length > 1 && <small>primary</small>}</li>)}
                {donor.contact.phones.map((p) => <li key={p}><Icon name="phone" size={14} /><a href={`tel:${p}`}>{p}</a></li>)}
                {addr && Object.values(addr).some(Boolean) && <li><Icon name="pin" size={14} /><span>{[addr.line1, addr.line2, [addr.city, addr.region].filter(Boolean).join(', '), addr.postalCode].filter(Boolean).join(' · ')}</span></li>}
                {!donor.contact.emails.length && !donor.contact.phones.length && !addr && <li className="fos-hint">No contact details on file.</li>}
              </ul>
            ) : <p className="fos-hint"><Icon name="lock" size={13} /> Contact details are restricted for your access.</p>}
          </Section>
          <PersonLinkPanel detail={detail} onChanged={refresh} />
        </aside>
      </div>

      {mode && rel && <AssignDrawer donorId={donor.id} relationship={rel} mode={mode} onClose={() => setMode(null)} onSaved={() => { setMode(null); refresh(); }} />}
      {editing && <DonorFormDrawer context={context} existing={detail} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />}
      {addingRel && <AddRelationship detail={detail} onClose={() => setAddingRel(false)} onSaved={(key) => { setAddingRel(false); setSelected(key); refresh(); }} />}
    </div>
  );
}

function AddRelationship({ detail, onClose, onSaved }: { detail: DonorDetail; onClose: () => void; onSaved: (scopeKey: string) => void }) {
  const [scopeKey, setScopeKey] = useState(detail.permissions.addRelationshipScopes[0]?.scopeKey ?? '');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setBusy(true); setError('');
    try { await fosApi.saveRelationship({ requestId: newRequestId(), operation: 'add', donorId: detail.donor.id, scope: scopeFromKey(scopeKey), ...(summary.trim() ? { summary } : {}) }); onSaved(scopeKey); }
    catch (cause) { setError(fosError(cause).message); } finally { setBusy(false); }
  };
  return (
    <Drawer title="Add a relationship" subtitle={detail.donor.displayName} onClose={onClose}>
      <div className="fos-form">
        <p className="fos-hint">Each relationship has its own coordinator, managers, notes and activity, visible only to people with access to that scope.</p>
        <label>Held by<select value={scopeKey} onChange={(e) => setScopeKey(e.target.value)}>{detail.permissions.addRelationshipScopes.map((s) => <option key={s.scopeKey} value={s.scopeKey}>{s.label}</option>)}</select></label>
        <label>Note <small className="fos-hint">(optional, private to this relationship)</small><textarea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1000} /></label>
        <ErrorNote message={error} />
        <div className="drawer-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} disabled={busy || !scopeKey}>{busy ? <Spinner /> : 'Add relationship'}</button></div>
      </div>
    </Drawer>
  );
}
